import { NgClass } from '@angular/common';
import { Component, inject, Input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService, SelectItem } from 'primeng/api';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { SERVICE_ENDPOINTS } from '../../../../../core/configs/service-endpoints';
import { I18nService } from '../../../../../core/services/i18n.service';
import { PaymentMethodName } from '../../../../enums/payment/payment-method-name';
import { PaymentData } from '../../../../interfaces/payment/payment-data';
import { PaymentTypeContractData } from '../../../../interfaces/payment/payment-type-contract-data';
import { PaymentConfigService } from '../../../../services/payment-config.service';
import { SettingsProviderService } from '../../../../services/settings-provider.service';

@Component({
  selector: 'admin-payment-type-contract',
  imports: [
    InputText,
    ReactiveFormsModule,
    Select,
    FormsModule,
    NgClass
  ],
  templateUrl: './admin-payment-type-contract.component.html'
})
export class AdminPaymentTypeContractComponent implements OnInit {

  loading: boolean = true;

  i18n = inject(I18nService);
  paymentConfigService = inject(PaymentConfigService);
  messageService = inject(MessageService);
  settingsProvider = inject(SettingsProviderService);
  protected readonly PaymentMethodName = PaymentMethodName;
  protected activeCardsList: SelectItem[] = [];

  @Input() paymentTypeContractData: PaymentTypeContractData;
  @Input() tenantId: string;

  activationEnabled = output<boolean>();

  ngOnInit() {
    this.checkValidContractData();
    if ((this.paymentTypeContractData.paymentMethod === PaymentMethodName.APPLEPAY) || (this.paymentTypeContractData.paymentMethod === PaymentMethodName.GOOGLEPAY)) {
      this.getActiveCreditCardContracts();
    } else {
      this.loading = false;
    }
  }

  private checkValidContractData() {
    // TODO - for all payment types
    let valid: boolean = true;
    switch (this.paymentTypeContractData.paymentMethod) {
      case PaymentMethodName.CREDITCARD:
      case PaymentMethodName.CREDITCARD_MOTO:
        if (!this.paymentTypeContractData?.vuNumber || this.paymentTypeContractData.vuNumber.trim().length === 0) {
          valid = false;
        }
        if (!valid) {
          this.messageService.add({
            life: 4000,
            severity: 'info',
            summary: this.i18n.i18nLng()['INFORMATION'],
            detail: this.i18n.i18nLng()['CONTRACTDATAINCOMPLETE_CC']
          });
        }
        break;
      case PaymentMethodName.PAYPAL:
        if (!this.paymentTypeContractData?.payPalEmailAddress || this.paymentTypeContractData.payPalEmailAddress.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.payPalMerchantId || this.paymentTypeContractData.payPalMerchantId.trim().length === 0) {
          valid = false;
        }
        if (!valid) {
          this.messageService.add({
            life: 4000,
            severity: 'info',
            summary: this.i18n.i18nLng()['INFORMATION'],
            detail: this.i18n.i18nLng()['CONTRACTDATAINCOMPLETE']
          });
        }
        break;
      case PaymentMethodName.SEPA:
        if (!this.paymentTypeContractData?.creditorId || this.paymentTypeContractData.creditorId.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.accountHolder || this.paymentTypeContractData.accountHolder.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.IBAN || this.paymentTypeContractData.IBAN.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.BIC || this.paymentTypeContractData.BIC.trim().length === 0) {
          valid = false;
        }
        if (!valid) {
          this.messageService.add({
            life: 4000,
            severity: 'info',
            summary: this.i18n.i18nLng()['INFORMATION'],
            detail: this.i18n.i18nLng()['CONTRACTDATAINCOMPLETE_SEPA']
          });
        }
        break;
    }
    this.validateData();
  }

  /**
   * Check if data input is complete
   * @protected
   */
  protected validateData() {
    let valid: boolean = true;
    switch (this.paymentTypeContractData.paymentMethod) {
      case PaymentMethodName.CREDITCARD:
      case PaymentMethodName.CREDITCARD_MOTO:
        if (!this.paymentTypeContractData.creditCardBrand) {
          valid = false;
        }
        if (!this.paymentTypeContractData.creditCardAcceptancePoint) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.vuNumber || this.paymentTypeContractData.vuNumber.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData.contractOptions) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.mcc || this.paymentTypeContractData.mcc.trim().length < 4) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.terminalId || this.paymentTypeContractData.terminalId.trim().length === 0) {
          valid = false;
        }
        break;
      case PaymentMethodName.PAYPAL:
        if (!this.paymentTypeContractData?.payPalEmailAddress || this.paymentTypeContractData.payPalEmailAddress.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.payPalMerchantId || this.paymentTypeContractData.payPalMerchantId.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.payPalPartnerId || this.paymentTypeContractData.payPalPartnerId.trim().length === 0) {
          valid = false;
        }
        break;
      case PaymentMethodName.SEPA:
        if (!this.paymentTypeContractData?.creditorId || this.paymentTypeContractData.creditorId.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.accountHolder || this.paymentTypeContractData.accountHolder.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.IBAN || this.paymentTypeContractData.IBAN.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.BIC || this.paymentTypeContractData.BIC.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.billingType) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.nameOnBankStatement || this.paymentTypeContractData.nameOnBankStatement.trim().length === 0) {
          valid = false;
        }
        if (!this.paymentTypeContractData?.nameOnSEPAMandate || this.paymentTypeContractData.nameOnSEPAMandate.trim().length === 0) {
          valid = false;
        }
        break;
      case PaymentMethodName.APPLEPAY:
      case PaymentMethodName.GOOGLEPAY:
        if (!this.paymentTypeContractData?.activeCreditCardId) {
          valid = false;
        }
    }
    this.activationEnabled.emit(valid);
  }

  /**
   * get all active Credit Card Contracts for the selected organization
   * Google Pay and Apple Pay need an active CC contract for activation
   * @private
   */
  private getActiveCreditCardContracts() {
    this.paymentConfigService.findAll(SERVICE_ENDPOINTS.GET_CC_WITH_ACTIVE_CONTRACT + this.tenantId).subscribe({
      next: (result) => {
        if (result.successful) {
          this.activeCardsList = [];
          (result.payload as PaymentData[]).map(paymentType => {
            this.activeCardsList.push({
              label: paymentType.title,
              value: paymentType.id
            });
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: error.message
        });
      }
    });
  }

}
